import { useState } from 'react';
import {
  Avatar,
  Box,
  ButtonBase,
  Divider,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Tooltip,
  Typography,
} from '@mui/material';
import AdminPanelSettingsOutlinedIcon from '@mui/icons-material/AdminPanelSettingsOutlined';
import KeyOutlinedIcon from '@mui/icons-material/KeyOutlined';
import LogoutIcon from '@mui/icons-material/Logout';
import NewReleasesOutlinedIcon from '@mui/icons-material/NewReleasesOutlined';
import { WhatsNewDialog } from './AppFooter';
import { APP_NAME, APP_VERSION, BUILT_BY } from '../lib/appInfo';
import { useAuthStore } from '../store/authStore';
import { AdminDialog } from './admin/AdminDialog';
import { ChangePasswordDialog } from './auth/AuthScreens';

function initials(name: string, email: string): string {
  const source = name.trim() || email.split('@')[0];
  const parts = source.split(/[\s._-]+/).filter(Boolean);
  return ((parts[0]?.[0] ?? '') + (parts[1]?.[0] ?? '')).toUpperCase() || '?';
}

export function UserMenu() {
  const user = useAuthStore((s) => s.user);
  const logout = useAuthStore((s) => s.logout);
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const [adminOpen, setAdminOpen] = useState(false);
  const [passwordOpen, setPasswordOpen] = useState(false);
  const [whatsNewOpen, setWhatsNewOpen] = useState(false);
  if (!user) return null;

  return (
    <>
      <Tooltip title="Account">
        <ButtonBase
          aria-label="Account menu"
          onClick={(e) => setAnchor(e.currentTarget)}
          sx={{ borderRadius: '50%', ml: 0.5 }}
        >
          <Avatar sx={{ width: 32, height: 32, fontSize: 13, fontWeight: 600, bgcolor: 'primary.main' }}>
            {initials(user.name, user.email)}
          </Avatar>
        </ButtonBase>
      </Tooltip>
      <Menu
        anchorEl={anchor}
        open={Boolean(anchor)}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{ paper: { sx: { width: 240 } } }}
      >
        <Box sx={{ px: 1.5, py: 1 }}>
          <Typography variant="body2" noWrap sx={{ fontWeight: 600 }}>
            {user.name || user.email.split('@')[0]}
          </Typography>
          <Typography variant="caption" noWrap component="div">
            {user.email} · {user.role === 'admin' ? 'Admin' : 'Member'}
          </Typography>
        </Box>
        <Divider sx={{ my: 0.5 }} />
        {user.role === 'admin' && (
          <MenuItem
            onClick={() => {
              setAnchor(null);
              setAdminOpen(true);
            }}
          >
            <ListItemIcon>
              <AdminPanelSettingsOutlinedIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText primary="Admin settings" />
          </MenuItem>
        )}
        <MenuItem
          onClick={() => {
            setAnchor(null);
            setPasswordOpen(true);
          }}
        >
          <ListItemIcon>
            <KeyOutlinedIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary="Change password" />
        </MenuItem>
        <MenuItem
          onClick={() => {
            setAnchor(null);
            setWhatsNewOpen(true);
          }}
        >
          <ListItemIcon>
            <NewReleasesOutlinedIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary="What's new" />
        </MenuItem>
        <MenuItem onClick={() => void logout()}>
          <ListItemIcon>
            <LogoutIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary="Sign out" />
        </MenuItem>
        <Divider sx={{ my: 0.5 }} />
        <Typography variant="caption" component="div" sx={{ px: 1.5, py: 0.5, color: 'text.disabled' }}>
          {APP_NAME} v{APP_VERSION} · {BUILT_BY}
        </Typography>
      </Menu>
      <WhatsNewDialog open={whatsNewOpen} onClose={() => setWhatsNewOpen(false)} />
      {user.role === 'admin' && <AdminDialog open={adminOpen} onClose={() => setAdminOpen(false)} />}
      <ChangePasswordDialog open={passwordOpen} onClose={() => setPasswordOpen(false)} />
    </>
  );
}
